import { Descendant, Editor, Element, Element as SlateElement, Text, Transforms } from 'slate';
import {
  CustomEditor,
  CustomElement,
  CustomElementType,
  CustomElementWithAlign,
  CustomText,
  CustomTextKey,
  HeadingElement,
  HeadingFiveElement,
  HeadingFourElement,
  HeadingSixElement,
  HeadingThreeElement,
  HeadingTwoElement,
  LinkElement,
  ListItemElement,
  ParagraphElement,
} from '../types/slate';

export const LIST_TYPES = ['numbered-list', 'bulleted-list'] as const;
export const TEXT_ALIGN_TYPES = ['left', 'center', 'right'] as const;
export const FONT_SIZE_OPTIONS = [
  { label: 10, value: 10 },
  { label: 12, value: 12 },
  { label: 14, value: 14 },
  { label: 16, value: 16 },
  { label: 18, value: 18 },
  { label: 20, value: 20 },
  { label: 22, value: 22 },
  { label: 24, value: 24 },
];

export type AlignType = (typeof TEXT_ALIGN_TYPES)[number];
export type ListType = (typeof LIST_TYPES)[number];
export type CustomElementFormat = CustomElementType | AlignType | ListType;

export const isAlignElement = (element: CustomElement): element is CustomElementWithAlign => {
  return element.hasOwnProperty('align');
};

export const isAlignType = (format: CustomElementFormat): format is AlignType => {
  return TEXT_ALIGN_TYPES.includes(format as AlignType);
};

export const isListType = (format: CustomElementFormat): format is ListType => {
  return LIST_TYPES.includes(format as ListType);
};

export const isMarkActive = (editor: CustomEditor, format: CustomTextKey) => {
  const marks = Editor.marks(editor);

  return marks ? marks[format] === true : false;
};

export const getHighlightedColor = (editor: CustomEditor) => {
  const marks = Editor.marks(editor);

  return marks?.['color'];
};

export const getHighlightedFontSize = (editor: CustomEditor) => {
  const marks = Editor.marks(editor);

  return marks?.['fontSize'] || 18;
};

export const isBlockActive = (editor: CustomEditor, format: CustomElementFormat, blockType: 'type' | 'align' = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => {
        if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
          if (blockType === 'align' && isAlignElement(n)) {
            return n.align === format;
          }
          return n.type === format;
        }
        return false;
      },
    })
  );

  return !!match;
};

export const toggleMark = (editor: CustomEditor, format: CustomTextKey) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const toggleBlock = (editor: CustomEditor, format: CustomElementFormat) => {
  const isActive = isBlockActive(editor, format, isAlignType(format) ? 'align' : 'type');
  const isList = isListType(format);

  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && isListType(n.type) && !isAlignType(format),
    split: true,
  });
  let newProperties: Partial<SlateElement>;
  if (isAlignType(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    };
  }
  Transforms.setNodes<SlateElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const extractClipboardData = (dt: DataTransfer) => {
  const types = dt.types || [];
  const has = (t: string) => Array.from(types).includes(t);

  const plain = has('text/plain') ? dt.getData('text/plain') : '';
  const html = has('text/html') ? dt.getData('text/html') : '';
  const rtf = has('text/rtf') ? dt.getData('text/rtf') : '';

  const files: File[] = [];

  for (const item of Array.from(dt.items || [])) {
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file && file.type.startsWith('image/')) files.push(file);
    }
  }

  const imagesFromHTML: File[] = [];

  return { plain, html, rtf, files, imagesFromHTML };
};

class SlateParser {
  private colorTable: Map<string, number> = new Map();
  private colorsCount: number = 0;
  private currentListType: 'bulleted' | 'numbered' | null = null;
  private listItemCounter: number = 0;

  constructor() {
    // Don't pre-initialize any colors - only add them when used
  }

  public convert(nodes: Descendant[]): string {
    const fontTable = '{\\fonttbl{\\f0 Roboto;}}';

    // RTF header
    let rtf = `{\\rtf1\\ansi\\ansicpg1252\\nouicompat\\deflang1033`;
    rtf += fontTable;

    const content = this.convertNodes(nodes);

    rtf += this.createColorTable();
    rtf += `\\viewkind4\\uc1\\pard\\sa200\\sl276\\slmult1\\f0 `;
    rtf += content;

    // RTF footer
    rtf += '}';

    return rtf;
  }

  private getColorIndex(color: string): number {
    const colorIndex = this.colorTable.get(color);

    if (colorIndex !== undefined) {
      return colorIndex;
    } else {
      const newIndex = this.colorsCount;
      this.colorTable.set(color, this.colorsCount);
      this.colorsCount++;
      return newIndex;
    }
  }

  private convertNodes(nodes: Descendant[]): string {
    const result = nodes.map((node) => this.convertNode(node)).join('');
    return result;
  }

  private convertNode(node: Descendant): string {
    if (Text.isText(node)) {
      return this.convertTextNode(node as CustomText);
    }

    if (Element.isElement(node)) {
      return this.convertElement(node as CustomElement);
    }

    return '';
  }

  private convertElement(element: CustomElement): string {
    switch (element.type) {
      case 'paragraph':
        return this.convertParagraph(element as ParagraphElement);

      case 'heading-one':
        return this.convertHeading(element as HeadingElement, 1);
      case 'heading-two':
        return this.convertHeading(element as HeadingTwoElement, 2);
      case 'heading-three':
        return this.convertHeading(element as HeadingThreeElement, 3);
      case 'heading-four':
        return this.convertHeading(element as HeadingFourElement, 4);
      case 'heading-five':
        return this.convertHeading(element as HeadingFiveElement, 5);
      case 'heading-six':
        return this.convertHeading(element as HeadingSixElement, 6);

      case 'bulleted-list':
        this.currentListType = 'bulleted';
        this.listItemCounter = 0;
        const bulletedResult = element.children.map((node) => this.convertNode(node)).join('');
        this.currentListType = null;
        return bulletedResult;
      case 'numbered-list':
        this.currentListType = 'numbered';
        this.listItemCounter = 0;
        const numberedResult = element.children.map((node) => this.convertNode(node)).join('');
        this.currentListType = null;
        return numberedResult;
      case 'list-item':
        return this.convertListItem(element as ListItemElement);

      case 'link':
        return this.convertLink(element as LinkElement);

      case 'editable-void':
        return '';

      default:
        return this.convertNodes((element as any).children);
    }
  }

  private convertParagraph(element: ParagraphElement): string {
    let rtf = '';

    if (element.align) {
      switch (element.align) {
        case 'center':
          rtf += '\\qc ';
          break;
        case 'right':
          rtf += '\\qr ';
          break;
        case 'justify':
          rtf += '\\qj ';
          break;
        default:
          rtf += '\\ql ';
      }
    }

    rtf += this.convertNodes(element.children);
    rtf += '\\par ';

    return rtf;
  }

  private convertTextNode(text: CustomText): string {
    let rtf = '';

    // Skip empty text nodes that only have formatting
    if (!text.text) {
      return rtf;
    }

    if (text.bold) rtf += '\\b ';
    if (text.italic) rtf += '\\i ';
    if (text.underline || text.underlined) rtf += '\\ul ';
    if (text.strikethrough) rtf += '\\strike ';
    if (text.color) {
      const colorIndex = this.getColorIndex(text.color);
      // RTF uses cf0 for default/auto color, so we offset by 1
      const rtfColorIndex = colorIndex + 1;
      rtf += `\\cf${rtfColorIndex} `;
    }
    if (text.fontSize) {
      rtf += `\\fs${text.fontSize} `;
    }

    rtf += text.text;

    if (text.strikethrough) rtf += '\\strike0 ';
    if (text.underline || text.underlined) rtf += '\\ul0 ';
    if (text.italic) rtf += '\\i0 ';
    if (text.bold) rtf += '\\b0 ';
    if (text.color) rtf += '\\cf0 ';
    if (text.fontSize) rtf += '\\fs0 ';

    return rtf;
  }

  private createColorTable(): string {
    // Sort by index to ensure correct order
    const sortedEntries = Array.from(this.colorTable.entries()).sort((a, b) => a[1] - b[1]);


    const colorTableList = sortedEntries.reduce<string>((acc, [color, index]) => {
      const [red, green, blue] = convertHexToRgb(color);
      return acc + `\\red${red}\\green${green}\\blue${blue};`;
    }, '');

    return `{\\colortbl;${colorTableList}}`;
  }

  private convertHeading(
    element: HeadingElement | HeadingTwoElement | HeadingThreeElement | HeadingFourElement | HeadingFiveElement | HeadingSixElement,
    level: number
  ): string {
    const fontSizes = [36, 32, 28, 26, 24, 22];
    const fontSize = fontSizes[level - 1] || 24;

    let rtf = `\\b\\fs${fontSize} `;

    if (element.align) {
      switch (element.align) {
        case 'center':
          rtf += '\\qc ';
          break;
        case 'right':
          rtf += '\\qr ';
          break;
        case 'justify':
          rtf += '\\qj ';
          break;
        default:
          rtf += '\\ql ';
      }
    }

    rtf += this.convertNodes(element.children);
    rtf += '\\b0\\fs24\\par ';

    return rtf;
  }

  private convertListItem(element: ListItemElement): string {
    let rtf = '';
    if (this.currentListType === 'numbered') {
      this.listItemCounter++;
      rtf = `\\li360 ${this.listItemCounter}.\\tab `;
    } else {
      rtf = '\\li360\\bullet\\tab ';
    }
    rtf += this.convertNodes(element.children);
    rtf += '\\par ';
    return rtf;
  }

  private convertLink(element: LinkElement): string {
    let rtf = `{\\field{\\*\\fldinst{HYPERLINK "${element.url}"}}{\\fldrslt{\\ul\\cf1 `;
    rtf += this.convertNodes(element.children);
    rtf += '}}}';
    return rtf;
  }
}

export const slateToRTF: (nodes: Descendant[]) => string = (nodes) => {
  const parser = new SlateParser();
  const result = parser.convert(nodes);
  return result;
};

export const convertHexToRgb = (hex: string): [number, number, number] => {
  const match = hex
    .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (_, r, g, b) => '#' + r + r + g + g + b + b)
    ?.substring(1)
    ?.match(/.{2}/g);

  if (!match) {
    return [0, 0, 0];
  }

  return match.map((x) => parseInt(x, 16)) as [number, number, number];
};

export const convertRgbToHex: (r: number, g: number, b: number) => `#${string}` = (r, g, b) => {
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const isEditorEmpty = (nodes: Descendant[]): boolean => {
  if (nodes.length === 0) {
    return true;
  }

  for (const node of nodes) {
    if (Element.isElement(node) && node.type === 'paragraph') {
      const isNodeEmpty = node.children.every((child) => Text.isText(child) && child.text.trim() === '');

      if (!isNodeEmpty) {
        return false;
      }
    } else {
      return false;
    }
  }

  return true;
};
